import { useRef } from 'react';
import { ExternalLink, Folder, FolderGit2 } from 'lucide-react';
import { NavLink } from 'react-router';

import { PageTitle } from '@/components/page-title';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFilestoreRepos } from '@/hooks/queries/filestore/use-filestore-repos';
import { useOrgStore } from '@/stores/org-store';
import { orgPath } from '@/utils/org-path';
import { formatBytes } from '@/utils/format-bytes';
import { forgejoRepoUrl } from '@/constants/filestore';
import { ROUTES } from '@/constants/routes';
import { FilestoreErrorBanner } from './components/filestore-error-banner';

const formatTimestamp = (value: string | null): string =>
  value
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

export const FilesPage = () => {
  const orgId = useOrgStore((state) => state.currentOrg?.orgid ?? null);
  const tableRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error, refetch } = useFilestoreRepos();
  const repos = data?.repos ?? [];

  return (
    <div className='flex w-full flex-col gap-6'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <div className='flex items-center gap-3'>
            <Folder className='size-8 shrink-0 text-foreground' />
            <PageTitle title='Files' />
          </div>
          <p className='mt-1 max-w-3xl text-sm text-muted-foreground'>
            Files stored by your workflows and uploads, organized into versioned
            repositories. Every write is recorded with full provenance.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className='py-12 text-center text-sm text-muted-foreground'>
          Loading file repositories…
        </div>
      ) : error ? (
        <FilestoreErrorBanner message={error.message} onRetry={refetch} />
      ) : repos.length === 0 ? (
        <div className='rounded-lg border border-dashed px-6 py-10 text-center text-sm text-muted-foreground'>
          No file repositories yet. Repositories are created automatically the
          first time a workflow or upload stores a file.
        </div>
      ) : (
        <Table tableRef={tableRef}>
          <TableHeader>
            <TableRow>
              <TableHead>Repository</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className='w-16' />
            </TableRow>
          </TableHeader>
          <TableBody>
            {repos.map((repo) => (
              <TableRow key={repo.name}>
                <TableCell className='font-medium'>
                  <NavLink
                    to={orgPath(
                      `${ROUTES.FILES.ROOT}/${encodeURIComponent(repo.name)}`,
                    )}
                    className='flex items-center gap-2 hover:underline'
                  >
                    <FolderGit2 className='size-4 shrink-0 text-muted-foreground' />
                    {repo.name}
                  </NavLink>
                </TableCell>
                <TableCell className='text-muted-foreground'>
                  {repo.default_branch}
                </TableCell>
                <TableCell className='text-muted-foreground'>
                  {formatBytes((repo.size_kib ?? 0) * 1024)}
                </TableCell>
                <TableCell className='text-muted-foreground'>
                  {formatTimestamp(repo.updated_at)}
                </TableCell>
                <TableCell>
                  {orgId && (
                    <Button
                      asChild
                      variant='ghost'
                      size='icon'
                      aria-label={`Open ${repo.name} in Forgejo`}
                    >
                      <a
                        href={forgejoRepoUrl(orgId, repo.name)}
                        target='_blank'
                        rel='noreferrer'
                        title='Open in Forgejo (advanced)'
                      >
                        <ExternalLink className='size-4' />
                      </a>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};
